<script setup lang="ts">
import type { Form } from '~/ui-blocks/Formalization/blocks/paymentModel/supplierInformation/SupplierInformation.schema'
import type { ReceiptModel } from '~/utils/receipt-model/ReceiptModel'
import type { SupplierOption, SupplierValue } from '~/utils/receipt-model/supplier'
import { useToast } from '@solfacil/girassol'
import { useQueryClient } from '@tanstack/vue-query'
import usePaymentModelSendBillingLetter from '~/store-v2/payment-model/usePaymentModelSendBillingLetter'
import useSuppliers from '~/store-v2/payment-model/useSuppliers'
import { MAX_SUPPLIER_EMAILS } from '~/ui-blocks/Formalization/blocks/paymentModel/paymentModel.constants'
import BillingLetter from '~/ui-blocks/Formalization/blocks/paymentModel/supplierInformation/BillingLetter.vue'
import SupplierSelect from '~/ui-blocks/Formalization/blocks/paymentModel/supplierInformation/SupplierSelect.vue'
import { createBillingLetterLinkFetcher, useBillingLetterLinkPolling } from '~/ui-blocks/Formalization/blocks/paymentModel/supplierInformation/useBillingLetterLinkPolling'
import VkitPaymentAlert from '~/ui-blocks/Formalization/blocks/paymentModel/supplierInformation/VkitPaymentAlert.vue'
import {
  buildSupplierOptions,
  getSelectedSupplierId,
  getSelectedSupplierName,
  resolveSupplierIsPartner,
} from '~/utils/receipt-model/supplier'

// props
const { form, paymentModel, disableField, projectId, readOnly = false } = defineProps<{
  form: Form
  paymentModel: ReceiptModel
  disableField: boolean
  projectId: string
  // Terminal financing: no billing letter is sent and the e-mail list is frozen.
  readOnly?: boolean
}>()

// initial base hooks
const { t } = useI18n()
const { createErrorToast, createSuccessToast } = useToast()
const queryClient = useQueryClient()

// initial variables & computeds

const emailLoading = ref(false)
const emailSuccess = ref(false)
const showEmailRequiredError = ref(false)
const polledBillingLetterLink = ref<string | null>(null)
const linkBeforeSend = ref<string | null>(null)
const billingLetterRef = ref<InstanceType<typeof BillingLetter> | null>(null)

// fields
const [hardwareSupplierModel, hardwareSupplierProps] = form.defineField('hardware_supplier')
const [invoiceValueModel, invoiceValueProps] = form.defineField('equipment_value')

const hardwareSupplierComputed = computed<SupplierValue | undefined>({
  get: () => {
    const value = hardwareSupplierModel.value
    if (!value) return undefined
    return {
      name: value.name ?? '',
      value: value.value ?? 0,
    }
  },
  set: (value) => {
    hardwareSupplierModel.value = value
  },
})

const selectedSupplierId = computed(() => getSelectedSupplierId(hardwareSupplierModel.value))
const selectedSupplierName = computed(() => getSelectedSupplierName(hardwareSupplierModel.value))
const hasSupplierSelected = computed(() => selectedSupplierId.value !== null)

const supplierEmails = computed(() =>
  (form.values.supplier_email ?? []).filter(email => typeof email === 'string' && email !== ''),
)

const supplierEmailError = computed(() => {
  if (showEmailRequiredError.value)
    return t('receipt-model.email_tags.required')

  const entry = Object.entries(form.errors.value)
    .find(([path, message]) => path.startsWith('supplier_email') && Boolean(message))
  return entry?.[1] || undefined
})

// domain hooks
const { data: suppliersData, isLoading: isLoadingSuppliers } = useSuppliers()
const { mutateAsync: mutateSendBillingLetter } = usePaymentModelSendBillingLetter()
const {
  start: startLinkPolling,
  stop: stopLinkPolling,
  isPolling: isPollingLink,
} = useBillingLetterLinkPolling({
  fetchLink: createBillingLetterLinkFetcher(queryClient, () => projectId, selectedSupplierId, linkBeforeSend),
})

const supplierOptions = computed<SupplierOption[]>(() =>
  suppliersData.value ? buildSupplierOptions(suppliersData.value, paymentModel.supplier) : [],
)

const selectedSupplierIsPartner = computed(() =>
  resolveSupplierIsPartner(selectedSupplierId.value, suppliersData.value, paymentModel.supplier),
)

const showPartnerAlert = computed(() =>
  !disableField && hasSupplierSelected.value && selectedSupplierIsPartner.value && Boolean(selectedSupplierName.value),
)
const showNotPartnerAlert = computed(() =>
  !disableField && hasSupplierSelected.value && !selectedSupplierIsPartner.value,
)

const showBillingLetter = computed(() => hasSupplierSelected.value && selectedSupplierIsPartner.value)

const isSavedSupplierSelected = computed(() =>
  hasSupplierSelected.value && selectedSupplierId.value === paymentModel.supplier?.id,
)
const savedBillingLetterLink = computed(() =>
  isSavedSupplierSelected.value ? (paymentModel.supplier?.billing_letter_link || null) : null,
)
const billingLetterLink = computed(() => {
  if (isPollingLink.value && linkBeforeSend.value)
    return polledBillingLetterLink.value
  return savedBillingLetterLink.value || polledBillingLetterLink.value
})

const isBillingLetterSent = computed(() => emailSuccess.value || Boolean(savedBillingLetterLink.value))

// functions
function onSupplierEmailsChange(emails: string[]) {
  if (readOnly)
    return

  form.setFieldValue('supplier_email', emails)
  emailSuccess.value = false
  showEmailRequiredError.value = false
}

function startBillingLetterLinkPolling() {
  const supplierId = selectedSupplierId.value
  polledBillingLetterLink.value = null

  void startLinkPolling().then((link) => {
    if (link && selectedSupplierId.value === supplierId)
      polledBillingLetterLink.value = link
  })
}

// Send
async function sendEmail(showSuccessMessage = true): Promise<void> {
  if (readOnly)
    return

  // Also called on the step's submit (PaymentModelStep `sendFiles`): no e-mail = silent no-op.
  if (!supplierEmails.value.length)
    return

  try {
    emailLoading.value = true
    const { valid, values } = await form.validate()
    // Only type narrowing: the schema already requires distributor and kit value.
    const supplierId = getSelectedSupplierId(values?.hardware_supplier)
    if (!valid || !values || supplierId === null || values.equipment_value === undefined)
      return

    stopLinkPolling()
    // Any GET link (even of another distributor) is not this send's letter: the polling waits for a different one.
    linkBeforeSend.value = paymentModel.supplier?.billing_letter_link || null

    await mutateSendBillingLetter({
      project_id: paymentModel.project_id,
      supplier_id: supplierId,
      supplier_email: values.supplier_email ?? [],
      equipment_value: values.equipment_value,
    })

    if (showSuccessMessage) {
      createSuccessToast({
        title: t('receipt-model.send-billing-letter.success.title'),
        description: t('receipt-model.send-billing-letter.success.description'),
      })
    }

    emailSuccess.value = true
    startBillingLetterLinkPolling()
  }
  catch (error) {
    console.error(error)
    createErrorToast({
      title: t('receipt-model.send-billing-letter.error.title'),
      description: t('receipt-model.send-billing-letter.error.description'),
    })
    form.setFieldError('supplier_email[0]', t('receipt-model.billing_letter.errors.send_failed'))
  }
  finally {
    emailLoading.value = false
  }
}

async function handleSendEmailClick() {
  if (readOnly)
    return

  const hasNoInvalidPending = billingLetterRef.value?.commitPending() ?? true
  if (!hasNoInvalidPending)
    return

  if (!supplierEmails.value.length) {
    showEmailRequiredError.value = true
    // Shows the kit value error (required) together with the e-mail one.
    await form.validate()
    billingLetterRef.value?.focus()
    return
  }

  await sendEmail()
}

// watchers
// Rehydrated only when the saved value changes: a refetch with the same data doesn't overwrite the user's edits.
watch(() => paymentModel.equipment_value, (equipmentValue) => {
  form.setValues({
    equipment_value: equipmentValue || 0,
  }, false)
}, { immediate: true })

watch(() => (paymentModel.supplier?.emails ?? []).join('\n'), () => {
  const emails = paymentModel.supplier?.emails ?? []
  if (emails.length) {
    emailSuccess.value = true
    form.setFieldValue('supplier_email', [...emails])
  }
}, { immediate: true })

watch(
  [suppliersData, () => paymentModel.supplier?.id],
  ([_suppliersData, savedId]) => {
    if (!_suppliersData || !savedId)
      return

    const savedOption = supplierOptions.value.find(option => option.value === savedId)
    if (savedOption) {
      form.setValues({
        hardware_supplier: { name: savedOption.name, value: savedOption.value },
      }, false)
    }
  },
  { immediate: true },
)

// Changing the distributor resets the send state; the first load (no distributor → saved one) is not a change.
watch(selectedSupplierId, (_supplierId, previousSupplierId) => {
  stopLinkPolling()
  polledBillingLetterLink.value = null

  if (previousSupplierId !== null)
    emailSuccess.value = false
})

defineExpose({
  sendEmail,
})
</script>

<template>
  <form
    class="supplier-information"
    data-testid="supplier-information-form"
    novalidate
    @submit.prevent
  >
    <section class="supplier-information__section" data-testid="supplier-information-equipment">
      <h3 class="supplier-information__title">
        {{ t('receipt-model.supplier_info.title') }}
      </h3>

      <VkitPaymentAlert
        v-if="paymentModel.is_vkit"
        :project-id="paymentModel.project_id"
        :transfers="paymentModel.transfers"
      />

      <div class="grid grid-cols-1 items-start gap-4xs md:system:grid-cols-3">
        <SupplierSelect
          id="hardwareSupplier"
          v-model="hardwareSupplierComputed"
          v-bind="hardwareSupplierProps"
          name="hardwareSupplier"
          class="md:system:col-span-2"
          :label="t('receipt-model.supplier_select.label')"
          :options="supplierOptions"
          :disabled="disableField"
          :loading="isLoadingSuppliers"
          :error="form.errors.value.hardware_supplier"
        />
        <InputMoneyInput
          id="invoiceValue"
          v-model="invoiceValueModel"
          v-bind="invoiceValueProps"
          class="w-full"
          name="invoiceValue"
          size="medium"
          required
          :disabled="disableField"
          :error="form.errors.value.equipment_value"
          :label="t('receipt-model.supplier_info.kit_value_label')"
        />
      </div>

      <SolAlert
        v-if="showPartnerAlert"
        id="supplier-partner-alert"
        class="supplier-information__supplier-alert"
        feedback="informative"
        :title="t('receipt-model.supplier_info.partner_alert.title', { name: selectedSupplierName })"
      >
        {{ t('receipt-model.supplier_info.partner_alert.text') }}
      </SolAlert>
      <SolAlert
        v-else-if="showNotPartnerAlert"
        id="supplier-not-partner-alert"
        class="supplier-information__supplier-alert"
        feedback="warning"
        :title="t('receipt-model.supplier_info.not_partner_alert.title')"
      >
        <span>{{ t('receipt-model.supplier_info.not_partner_alert.text') }}</span>
        <span>{{ t('receipt-model.supplier_info.not_partner_alert.text_complement') }}</span>
      </SolAlert>
    </section>

    <BillingLetter
      v-if="showBillingLetter"
      ref="billingLetterRef"
      :supplier-name="selectedSupplierName"
      :emails="supplierEmails"
      :max-emails="MAX_SUPPLIER_EMAILS"
      :email-error="supplierEmailError"
      :locked="disableField"
      :sent="isBillingLetterSent"
      :download-link="billingLetterLink"
      :sending="emailLoading"
      :link-pending="isPollingLink"
      @update:emails="onSupplierEmailsChange"
      @send="handleSendEmailClick()"
    />
  </form>
</template>

<style lang="scss" scoped>
.supplier-information {
  @apply flex flex-col gap-xs;

  &__section {
    @apply flex flex-col gap-4xs;
  }

  // Fixed 20px (Grotesk): `fonts-heading-h3` grows to 24px from 1024px.
  &__title {
    @apply font-highlight font-regular text-xs leading-xs text-neutral-low-dark;
  }

  &__supplier-alert {
    @apply mt-3xs;
  }
}
</style>
